import { secureApi } from './secureApi';

/**
 * Owner Billing API — the internal accounting worksheet.
 *
 * Backend routes: be/routes/ownerBillingRoutes.js (mounted at /api/v1/owner-billing)
 *
 * All endpoints are JWT-authenticated, so this file uses `secureApi` only — the shared
 * instance whose interceptor attaches `x-auth-token` and runs the silent 401
 * refresh-retry. (`/finance` still calls raw axios with manual Bearer headers; that is
 * pre-existing debt tracked in tasks/legacy-axios-cleanup.md and not touched here.)
 *
 * The access boundary is enforced SERVER-side — BILLING_READ_ROLES to read and write
 * back what the accountant issued, BILLING_MANAGE_ROLES to finalise a month, discount
 * it, re-open it or edit a billing profile. Nothing here re-implements that.
 *
 * `preserveBackendError` on every mutation: secureApi rewrites 4xx bodies to generic
 * Arabic messages by default, which would hide the specific reason an action was
 * refused ("the month has not closed", "already finalised", "a discount needs a
 * reason"). On a page about money the specific reason IS the useful part.
 *
 * ⚠️ Raised timeouts on the two calls that run the builder. secureApi's default is 10s;
 * `POST /records` builds the worksheet TWICE (a preview to refuse an empty or
 * over-discounted month, then the real build it freezes). A client-side timeout on a
 * write that actually committed is the worst outcome available here — the operator is
 * told it failed, retries, and gets "already finalised". Same reasoning and same
 * precedent as `ownerReportApi.regenerateReport`.
 */

/** Long enough that a slow month never reports failure on a write that committed. */
const BUILDER_TIMEOUT_MS = 60000;

const ownerBillingApi = {

  /** Billable owners, for the picker. Five fields, no personal data. */
  getOwners: async () => {
    try {
      const response = await secureApi.get('/api/v1/owner-billing/owners');
      return response.data;
    } catch (error) {
      console.error('Error fetching billable owners:', error);
      throw error;
    }
  },

  /**
   * The worksheet for one owner + month.
   *
   * Answers `state: 'draft' | 'final'` — draft is computed live and moves as the
   * underlying data moves; final is read from the frozen record. Both arrive in the
   * SAME shape, so the page renders one thing either way.
   */
  getWorksheet: async (owner, month) => {
    try {
      const response = await secureApi.get('/api/v1/owner-billing/worksheet', {
        params: { owner, month },
        timeout: BUILDER_TIMEOUT_MS
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching billing worksheet:', error);
      throw error;
    }
  },

  /** Finalise the month — ops declaring the figures settled. Managers only. */
  finalise: async ({ owner, periodMonth, discount }) => {
    try {
      const response = await secureApi.post(
        '/api/v1/owner-billing/records',
        { owner, periodMonth, discount },
        { preserveBackendError: true, timeout: BUILDER_TIMEOUT_MS }
      );
      return response.data;
    } catch (error) {
      console.error('Error finalising billing month:', error);
      throw error;
    }
  },

  /** Re-open a finalised month. Managers only; the reason is required. */
  reopen: async (recordId, reason) => {
    try {
      const response = await secureApi.post(
        `/api/v1/owner-billing/records/${recordId}/cancel`,
        { reason },
        { preserveBackendError: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error re-opening billing month:', error);
      throw error;
    }
  },

  /** What the accountant issued: number, date, total. Nothing else is writable. */
  recordExternalInvoice: async (recordId, { invoiceNumber, invoiceDate, issuedTotal }) => {
    try {
      const response = await secureApi.patch(
        `/api/v1/owner-billing/records/${recordId}/external`,
        { invoiceNumber, invoiceDate, issuedTotal },
        { preserveBackendError: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error recording external invoice:', error);
      throw error;
    }
  },

  /** The customer tax identity. Managers only — not the accountant's to edit. */
  updateBillingProfile: async (ownerId, profile) => {
    try {
      const response = await secureApi.patch(
        `/api/v1/owner-billing/owners/${ownerId}/billing-profile`,
        profile,
        { preserveBackendError: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating billing profile:', error);
      throw error;
    }
  },

  /**
   * «حسابات المنصات والتسجيل الضريبي» — the owner's OTA accounts (units and +15% rule read from the
   * Airbnb copy) and their VAT change rows. Record-only; not month-bound. Billing viewers.
   * `preserveBackendError` here too: the box shows the server's own sentence in its own error state.
   */
  getOtaAccounts: async (ownerId) => {
    try {
      const response = await secureApi.get(
        `/api/v1/owner-billing/owners/${ownerId}/ota-accounts`,
        { preserveBackendError: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching OTA accounts:', error);
      throw error;
    }
  },

  /** Add one VAT change row. Managers only. NOT idempotent — never retried (see useOtaAccounts). */
  addOtaVatChange: async (ownerId, change) => {
    try {
      const response = await secureApi.post(
        `/api/v1/owner-billing/owners/${ownerId}/ota-vat-changes`,
        change,
        { preserveBackendError: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error recording OTA VAT change:', error);
      throw error;
    }
  },

  /** Withdraw a wrong row — kept as history, stops counting. Managers only; the reason is required. */
  withdrawOtaVatChange: async (ownerId, changeId, reason) => {
    try {
      const response = await secureApi.post(
        `/api/v1/owner-billing/owners/${ownerId}/ota-vat-changes/${changeId}/withdraw`,
        { reason },
        { preserveBackendError: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error withdrawing OTA VAT change:', error);
      throw error;
    }
  }
};

export { ownerBillingApi };
export default ownerBillingApi;
